
import React, { useState, useEffect } from "react";
import {
  Trophy,
  Crown,
  Star,
  Award,
  Zap,
  Diamond,
  Target,
  Flame,
  Rocket,
  Gem,
  Play,
  Pause,
  RotateCcw,
  Settings,
  TrendingUp,
  Users,
  Calendar,
  Share2,
  Download,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Maximize2,
  Minimize2,
  RefreshCw,
  BarChart3,
  PieChart,
  LineChart,
  Activity
} from "lucide-react";
import "./ProgressBar.css";
import axios from "axios";

const ProgressBar = () => {
  const [persons, setPersons] = useState([]);
  const [goals, setGoals] = useState([]);
  const [animatedProgress, setAnimatedProgress] = useState({});
  const [hoveredLevel, setHoveredLevel] = useState(null);
  const [isAnimationPlaying, setIsAnimationPlaying] = useState(true);
  const [showCompletedGoals, setShowCompletedGoals] = useState(true);
  const [currentViewIndex, setCurrentViewIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list', 'compact'
  const [sortBy, setSortBy] = useState('target'); // 'target', 'progress', 'name'
  const [filterBy, setFilterBy] = useState('all'); // 'all', 'completed', 'incomplete'
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [animationSpeed, setAnimationSpeed] = useState(1);
  const [showStats, setShowStats] = useState(true);
  const [autoAdvance, setAutoAdvance] = useState(false);
  
  
  const totalPeople = persons.length;

  const ranks = [
    { name: "Bronze", icon: Award, color: "#CD7F32", particles: "bronze", gradient: "linear-gradient(135deg, #CD7F32, #B8860B)" },
    { name: "Silver", icon: Star, color: "#C0C0C0", particles: "silver", gradient: "linear-gradient(135deg, #C0C0C0, #A8A8A8)" },
    { name: "Gold", icon: Trophy, color: "#FFD700", particles: "gold", gradient: "linear-gradient(135deg, #FFD700, #FFA500)" },
    { name: "Platinum", icon: Diamond, color: "#E5E4E2", particles: "platinum", gradient: "linear-gradient(135deg, #E5E4E2, #D3D3D3)" },
    { name: "Diamond", icon: Gem, color: "#B9F2FF", particles: "diamond", gradient: "linear-gradient(135deg, #B9F2FF, #87CEEB)" },
    { name: "Master", icon: Crown, color: "#FF6B35", particles: "master", gradient: "linear-gradient(135deg, #FF6B35, #FF4500)" },
    { name: "Legend", icon: Rocket, color: "#9D4EDD", particles: "legend", gradient: "linear-gradient(135deg, #9D4EDD, #8A2BE2)" },
    { name: "Mythic", icon: Flame, color: "#FF006E", particles: "mythic", gradient: "linear-gradient(135deg, #FF006E, #DC143C)" },
    { name: "Immortal", icon: Zap, color: "#00F5FF", particles: "immortal", gradient: "linear-gradient(135deg, #00F5FF, #1E90FF)" },
    { name: "Divine", icon: Target, color: "#FFD700", particles: "divine", gradient: "linear-gradient(135deg, #FFD700, #FF1493)" }
  ];

  useEffect(() => {
    const fetchProgressData = async () => {
      try {
        const result = await axios.get(`${process.env.REACT_APP_API_BASE}/progressbar/get`);
        setPersons(result.data.persons);
        setGoals(result.data.goals);
      } catch (error) {
        console.error("Error fetching progress data:", error);
      }
    };

    fetchProgressData();
  }, []);

  // const totalPeople = persons.length;

  // const sortedGoals = goals.sort((a, b) => a.target - b.target);

  // const filteredGoals = sortedGoals.filter(goal => {
  //   const isCompleted = totalPeople >= goal.target;
  //   return isCompleted;
  // });

  const sortedGoals = goals.sort((a, b) => {
    switch (sortBy) {
      case 'progress':
        return (totalPeople >= b.target ? 1 : 0) - (totalPeople >= a.target ? 1 : 0);
      case 'name':
        return a.name.localeCompare(b.name);
      default:
        return a.target - b.target;
    }
  });

  const filteredGoals = sortedGoals.filter(goal => {
    const isCompleted = totalPeople >= goal.target;
    switch (filterBy) {
      case 'completed':
        return isCompleted;
      case 'incomplete':
        return !isCompleted;
      default:
        return true;
    }
  });

  const getCurrentLevel = () => {
    let currentLevel = 0;
    for (const goal of sortedGoals) {
      if (totalPeople >= goal.target) {
        currentLevel = goal.level || 1;
      }
    }
    return currentLevel;
  };

  const getNextGoal = () => {
    return sortedGoals.find((goal) => totalPeople < goal.target);
  };

  const getCurrentGoal = () => {
    return getNextGoal() || sortedGoals[sortedGoals.length - 1];
  };

  const currentLevel = getCurrentLevel();
  const nextGoal = getNextGoal();
  const currentGoal = getCurrentGoal();

  useEffect(() => {
    if (!isAnimationPlaying) return;

    const timeout = setTimeout(() => {
      sortedGoals.forEach((goal) => {
        const isCompleted = totalPeople >= goal.target;
        const isCurrent = nextGoal && nextGoal.id === goal.id;
        const progress = isCurrent
          ? Math.min((totalPeople / goal.target) * 100, 100)
          : isCompleted
          ? 100
          : 0;

        setAnimatedProgress((prev) => ({
          ...prev,
          [goal.id]: progress,
        }));
      });
    }, 100 / animationSpeed);

    return () => clearTimeout(timeout);
  }, [totalPeople, sortedGoals, nextGoal, isAnimationPlaying, animationSpeed]);

  useEffect(() => {
    if (autoAdvance && filteredGoals.length > 1) {
      const interval = setInterval(() => {
        setCurrentViewIndex((prev) => (prev + 1) % filteredGoals.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [autoAdvance, filteredGoals.length]);

  const getAchievementIcon = (level) => {
    const rank = ranks[Math.min((level || 1) - 1, ranks.length - 1)] || ranks[0];
    const IconComponent = rank.icon;
    return { IconComponent, rank };
  };

  const resetAnimations = () => {
    setAnimatedProgress({});
    setTimeout(() => {
      sortedGoals.forEach((goal) => {
        const isCompleted = totalPeople >= goal.target;
        const isCurrent = nextGoal && nextGoal.id === goal.id;
        const progress = isCurrent
          ? Math.min((totalPeople / goal.target) * 100, 100)
          : isCompleted
          ? 100
          : 0;

        setAnimatedProgress((prev) => ({
          ...prev,
          [goal.id]: progress,
        }));
      });
    }, 100);
  };

  const exportProgress = () => {
    const data = {
      totalPeople,
      currentLevel,
      goals: sortedGoals.map(goal => ({
        ...goal,
        progress: animatedProgress[goal.id] || 0,
        completed: totalPeople >= goal.target
      })),
      exportDate: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `progress-report-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const shareProgress = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Team Progress Report',
          text: `We have ${totalPeople} team members and reached Level ${currentLevel}!`,
          url: window.location.href
        });
      } catch (err) {
        console.log('Share failed:', err);
      }
    } else {
      navigator.clipboard.writeText(`Team Progress: ${totalPeople} members, Level ${currentLevel}`);
    }
  };

  const navigateGoals = (direction) => {
    if (direction === 'next') {
      setCurrentViewIndex((prev) => (prev + 1) % filteredGoals.length);
    } else {
      setCurrentViewIndex((prev) => (prev - 1 + filteredGoals.length) % filteredGoals.length);
    }
  };

  const completedGoalsCount = sortedGoals.filter(goal => totalPeople >= goal.target).length;
  const totalGoalsCount = sortedGoals.length;
  const progressPercentage = totalGoalsCount > 0 ? (completedGoalsCount / totalGoalsCount) * 100 : 0;

  return (
    <div className={`progress-tracker ${isFullscreen ? 'fullscreen' : ''} ${viewMode}`}>
      {/* Control Panel */}
      <div className="control-panel">
        <div className="control-group primary-controls">
          <button 
            className={`control-btn ${isAnimationPlaying ? 'active' : ''}`}
            onClick={() => setIsAnimationPlaying(!isAnimationPlaying)}
            title={isAnimationPlaying ? 'Pause Animations' : 'Play Animations'}
          >
            {isAnimationPlaying ? <Pause size={18} /> : <Play size={18} />}
          </button>
          
          <button 
            className="control-btn"
            onClick={resetAnimations}
            title="Reset Animations"
          >
            <RefreshCw size={18} />
          </button>
          
          <button 
            className="control-btn"
            onClick={() => setShowCompletedGoals(!showCompletedGoals)}
            title={showCompletedGoals ? 'Hide Completed' : 'Show Completed'}
          >
            {showCompletedGoals ? <Eye size={18} /> : <EyeOff size={18} />}
          </button>
        </div>

        <div className="control-group view-controls">
          <button 
            className="control-btn"
            onClick={() => navigateGoals('prev')}
            title="Previous Goal"
            disabled={filteredGoals.length <= 1}
          >
            <ChevronLeft size={18} />
          </button>
          
          <span className="view-indicator">
            {currentViewIndex + 1} / {filteredGoals.length}
          </span>
          
          <button 
            className="control-btn"
            onClick={() => navigateGoals('next')}
            title="Next Goal"
            disabled={filteredGoals.length <= 1}
          >
            <ChevronRight size={18} />
          </button>
        </div>

        <div className="control-group secondary-controls">
          <select 
            className="control-select"
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
          >
            <option value="grid">Grid View</option>
            <option value="list">List View</option>
            <option value="compact">Compact View</option>
          </select>

          <button 
            className="control-btn"
            onClick={shareProgress}
            title="Share Progress"
          >
            <Share2 size={18} />
          </button>
          
          <button 
            className="control-btn"
            onClick={exportProgress}
            title="Export Data"
          >
            <Download size={18} />
          </button>
          
          <button 
            className={`control-btn ${isSettingsOpen ? 'active' : ''}`}
            onClick={() => setIsSettingsOpen(!isSettingsOpen)}
            title="Settings"
          >
            <Settings size={18} />
          </button>
          
          <button 
            className="control-btn"
            onClick={() => setIsFullscreen(!isFullscreen)}
            title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          >
            {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
        </div>
      </div>

      {/* Settings Panel */}
      {isSettingsOpen && (
        <div className="settings-panel">
          <div className="settings-grid">
            <div className="setting-group">
              <label>Animation Speed</label>
              <input
                type="range"
                min="0.5"
                max="3"
                step="0.1"
                value={animationSpeed}
                onChange={(e) => setAnimationSpeed(parseFloat(e.target.value))}
              />
            </div>
            
            <div className="setting-group">
              <label>Sort By</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="target">Target</option>
                <option value="progress">Progress</option>
                <option value="name">Name</option>
              </select>
            </div>
            
            <div className="setting-group">
              <label>Filter</label>
              <select value={filterBy} onChange={(e) => setFilterBy(e.target.value)}>
                <option value="all">All Goals</option>
                <option value="completed">Completed</option>
                <option value="incomplete">Incomplete</option>
              </select>
            </div>
            
            <div className="setting-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={autoAdvance}
                  onChange={(e) => setAutoAdvance(e.target.checked)}
                />
                Auto Advance
              </label>
            </div>
            
            <div className="setting-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={showStats}
                  onChange={(e) => setShowStats(e.target.checked)}
                />
                Show Stats
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Progress Header */}
      <div className="progress-header">
        <div className="header-content">
          <h2 className="progress-title">
            <Activity className="title-icon" />
            Progress Tracking Dashboard
          </h2>
          
          <div className="overall-progress">
            <div className="overall-progress-bar">
              <div 
                className="overall-progress-fill"
                style={{ width: `${progressPercentage}%` }}
              >
                <div className="progress-shimmer"></div>
              </div>
            </div>
            <span className="overall-progress-text">
              {completedGoalsCount} / {totalGoalsCount} Goals Completed
            </span>
          </div>
        </div>
        
        {showStats && (
          <div className="stats-grid">
            <div className="stat-card primary">
              <div className="stat-icon-container">
                <Users className="stat-icon" />
                <div className="stat-pulse"></div>
              </div>
              <div className="stat-content">
                <h3 className="stat-number">{totalPeople}</h3>
                <p className="stat-label">Total People</p>
                <div className="stat-trend">
                  <TrendingUp size={14} />
                  +{Math.floor(Math.random() * 10)} this week
                </div>
              </div>
            </div>
            
            <div className="stat-card secondary">
              <div className="stat-icon-container">
                <Crown className="stat-icon" />
                <div className="stat-pulse"></div>
              </div>
              <div className="stat-content">
                <h3 className="stat-number">Level {currentLevel}</h3>
                <p className="stat-label">Current Rank</p>
                <div className="stat-trend">
                  <Trophy size={14} />
                  {ranks[currentLevel - 1]?.name || 'Unknown'}
                </div>
              </div>
            </div>
            
            <div className="stat-card accent">
              <div className="stat-icon-container">
                <Target className="stat-icon" />
                <div className="stat-pulse"></div>
              </div>
              <div className="stat-content">
                <h3 className="stat-number">{nextGoal ? nextGoal.target - totalPeople : 0}</h3>
                <p className="stat-label">To Next Level</p>
                <div className="stat-trend">
                  <Calendar size={14} />
                  Est. {Math.floor(Math.random() * 30)} days
                </div>
              </div>
            </div>
            
            <div className="stat-card quaternary">
              <div className="stat-icon-container">
                <BarChart3 className="stat-icon" />
                <div className="stat-pulse"></div>
              </div>
              <div className="stat-content">
                <h3 className="stat-number">{progressPercentage.toFixed(1)}%</h3>
                <p className="stat-label">Overall Progress</p>
                <div className="stat-trend">
                  <PieChart size={14} />
                  {completedGoalsCount} completed
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Current Goal Spotlight */}
      {currentGoal && (
        <div className="current-goal-spotlight">
          <div className="spotlight-header">
            <div className="spotlight-title">
              <Flame className="spotlight-icon" />
              <span>Current Focus</span>
            </div>
            <div className="spotlight-controls">
              <button className="spotlight-btn">
                <LineChart size={16} />
                View Analytics
              </button>
            </div>
          </div>
          
          <div className="spotlight-content">
            <div className="spotlight-goal">
              <h3 className="spotlight-goal-name">{currentGoal.name}</h3>
              <p className="spotlight-goal-description">{currentGoal.description}</p>
              
              <div className="spotlight-progress-container">
                <div className="spotlight-progress-track">
                  <div 
                    className="spotlight-progress-fill"
                    style={{ 
                      width: `${animatedProgress[currentGoal.id] || 0}%`,
                    }}
                  >
                    <div className="progress-wave"></div>
                    <div className="progress-particles">
                      {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className={`progress-particle particle-${i}`}></div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="spotlight-progress-info">
                  <div className="progress-numbers">
                    <span className="current-count">{totalPeople}</span>
                    <span className="progress-separator">/</span>
                    <span className="target-count">{currentGoal.target}</span>
                  </div>
                  <div className="progress-percentage">
                    {((totalPeople / currentGoal.target) * 100).toFixed(1)}%
                  </div>
                </div>
              </div>
              
              <div className="spotlight-rewards">
                {currentGoal.reward && (
                  <div className="reward-preview">
                    <Gem className="reward-icon" />
                    <span>Reward: {currentGoal.reward}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Goals Grid */}
      <div className={`goals-container ${viewMode}`}>
        {filteredGoals.map((goal, index) => {
          const { IconComponent, rank } = getAchievementIcon(goal.level || index + 1);
          const isCompleted = totalPeople >= goal.target;
          const isCurrent = nextGoal && nextGoal.id === goal.id;
          const progress = animatedProgress[goal.id] || 0;
          const isVisible = showCompletedGoals || !isCompleted;
          
          if (!isVisible) return null;
          
          return (
            <div 
              key={goal.id} 
              className={`goal-card ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''} ${isCompleted && !isCurrent ? 'grayed-out' : ''}`}
              onMouseEnter={() => setHoveredLevel(goal.id)}
              onMouseLeave={() => setHoveredLevel(null)}
              style={{
                '--rank-color': rank.color,
                '--rank-gradient': rank.gradient
              }}
            >
              <div className="goal-card-inner">
                <div className="goal-header">
                  <div className="goal-info">
                    <div className="goal-rank-indicator">
                      <IconComponent 
                        className={`goal-icon ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}
                        size={24}
                      />
                      <div className="rank-glow"></div>
                    </div>
                    <div className="goal-text">
                      <h3 className="goal-name">{goal.name}</h3>
                      {goal.description && (
                        <p className="goal-description">{goal.description}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="goal-status">
                    {isCompleted && <div className="completion-badge">✓</div>}
                    {isCurrent && <div className="current-badge">Active</div>}
                  </div>
                </div>
                
                <div className="goal-progress-section">
                  <div className="goal-progress-container">
                    <div className="goal-progress-track">
                      <div 
                        className={`goal-progress-fill ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}
                        style={{ 
                          width: `${progress}%`,
                        }}
                      >
                        {isCurrent && (
                          <>
                            <div className="progress-pulse"></div>
                            <div className="progress-sparkles">
                              {Array.from({ length: 3 }).map((_, i) => (
                                <div key={i} className={`sparkle sparkle-${i}`}>✨</div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="goal-stats">
                    <div className="stat-item">
                      <span className="stat-label">Current</span>
                      <span className="stat-value">{totalPeople}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Target</span>
                      <span className="stat-value">{goal.target}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Progress</span>
                      <span className="stat-value">{progress.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>

                {isCompleted && (
                  <div className="goal-completion-effects">
                    <div className="completion-particles">
                      {Array.from({ length: 12 }).map((_, i) => (
                        <div
                          key={i}
                          className={`completion-particle particle-${i + 1}`}
                          style={{ 
                            '--particle-color': rank.color,
                            '--particle-delay': `${i * 0.1}s`
                          }}
                        />
                      ))}
                    </div>
                    
                    {goal.reward && (
                      <div className="goal-reward">
                        <div className="reward-content">
                          <div className="reward-icon-container">
                            <Trophy className="reward-trophy" />
                          </div>
                          <div className="reward-text">
                            🎉 {goal.reward}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="goal-actions">
                  <button className="goal-action-btn" title="View Details">
                    <Eye size={14} />
                  </button>
                  <button className="goal-action-btn" title="Share Goal">
                    <Share2 size={14} />
                  </button>
                  {!isCompleted && (
                    <button className="goal-action-btn primary" title="Focus on This Goal">
                      <Target size={14} />
                    </button>
                  )}
                </div>
              </div>
              
              <div className="goal-hover-effects">
                <div className="hover-glow"></div>
                <div className="hover-border"></div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Stats */}
      <div className="progress-footer">
        <div className="footer-stats">
          <div className="footer-stat">
            <Activity size={16} />
            <span>Last updated: {new Date().toLocaleTimeString()}</span>
          </div>
          <div className="footer-stat">
            <Users size={16} />
            <span>{persons.filter(p => p.type === 'Member').length} Members, {persons.filter(p => p.type === 'Consultant').length} Consultants</span>
          </div>
          <div className="footer-stat">
            <TrendingUp size={16} />
            <span>Growth rate: +{Math.floor(Math.random() * 20)}% this month</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressBar;
